import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, CircularProgress, Box } from '@mui/material';
import styled from '@emotion/styled';
import { getDeactivationImpact } from '../redux/slices/users';

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    color: theme.palette.text.secondary,
}));

const ImpactList = styled('ul')(({ theme }) => ({
    margin: theme.spacing(1.5, 0, 0),
    paddingLeft: theme.spacing(2.5),
    color: theme.palette.text.secondary,
}));

const ImpactItem = styled('li')(({ theme }) => ({
    marginBottom: theme.spacing(0.5),
}));

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const DeactivateUserDialog = ({ open, user, onClose, onConfirm }) => {
    const dispatch = useDispatch();
    const [impact, setImpact] = useState(null);
    const [isLoadingImpact, setIsLoadingImpact] = useState(false);
    const [impactFailed, setImpactFailed] = useState(false);

    const userName = user ? (user.fullName || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email) : 'this user';

    useEffect(() => {
        if (!open || !user?._id) return;
        setImpact(null);
        setImpactFailed(false);
        setIsLoadingImpact(true);
        dispatch(getDeactivationImpact({
            userId: user._id,
            callback: (data) => {
                setIsLoadingImpact(false);
                if (data) {
                    setImpact(data);
                } else {
                    setImpactFailed(true);
                }
            },
        }));
    }, [open, user, dispatch]);

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm();
        }
        onClose();
    };

    const hasImpact = impact && (
        impact.accountsManaged || impact.clientsManaged || impact.projectsResponsible || impact.tasksResponsible || impact.runningTimers
    );

    // "…anyway" only reads right when we're overriding a warning; on the confirmed-safe
    // branch (impact loaded, nothing owned) a plain "Deactivate" is correct.
    const confirmLabel = impact && !hasImpact ? 'Deactivate' : 'Deactivate anyway';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Deactivate {userName}?</DialogTitle>
            <DialogContent>
                {isLoadingImpact ? (
                    <LoadingRow>
                        <CircularProgress size={18} />
                        Checking what this user currently owns…
                    </LoadingRow>
                ) : impactFailed || !impact ? (
                    <DialogContentText>
                        Deactivating {userName} will unassign them from everything they currently own — the accounts and
                        clients they manage will be cleared, any projects and tasks they are responsible for will be
                        unassigned, and any running timers will be stopped. This can be reversed by reactivating them later.
                    </DialogContentText>
                ) : hasImpact ? (
                    <>
                        <DialogContentText>
                            Deactivating {userName} will unassign them from everything they currently own:
                        </DialogContentText>
                        <ImpactList>
                            {impact.accountsManaged > 0 && (
                                <ImpactItem>Manages {plural(impact.accountsManaged, 'account')} — these will be cleared.</ImpactItem>
                            )}
                            {impact.clientsManaged > 0 && (
                                <ImpactItem>Manages {plural(impact.clientsManaged, 'client')} — these will be cleared.</ImpactItem>
                            )}
                            {impact.projectsResponsible > 0 && (
                                <ImpactItem>Responsible for {plural(impact.projectsResponsible, 'project')} — these will be unassigned.</ImpactItem>
                            )}
                            {impact.tasksResponsible > 0 && (
                                <ImpactItem>Responsible for {plural(impact.tasksResponsible, 'task')} — these will be unassigned.</ImpactItem>
                            )}
                            {impact.runningTimers > 0 && (
                                <ImpactItem>{plural(impact.runningTimers, 'running timer')} will be stopped.</ImpactItem>
                            )}
                        </ImpactList>
                    </>
                ) : (
                    <DialogContentText>
                        {userName} does not currently own any accounts, clients, projects, tasks, or running timers.
                        You can safely deactivate them.
                    </DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleConfirm} color="error" variant="contained">
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeactivateUserDialog;
